"use client";

import React, {
  createElement,
  CSSProperties,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { autocomplete } from "@algolia/autocomplete-js";
import { createRoot } from "react-dom/client";
import { useRouter, useSearchParams } from "next/navigation";
import classnames from "classnames";
import { AutocompleteOptions } from "@algolia/autocomplete-js";
import { IndexDocument } from "@cappelendamm/catalogue/meilisearch/search-index-receiver.js";
import { getSourcesWithFilters } from "./GetSources";
import { createLocalStorageRecentSearchesPlugin } from "@algolia/autocomplete-plugin-recent-searches";

import { getSearchParamsFromUrl } from "@/navigation/selectors";

import "@algolia/autocomplete-theme-classic";

/* Autocomplete is used in SearchBar.tsx, and you find its configuration here, like the plugins and the autocomplete instance.

Followed this guide to build this component: https://www.algolia.com/doc/ui-libraries/autocomplete/integrations/with-react-instantsearch/?client=Autocomplete.tsx */

export type AutocompleteProps = Partial<AutocompleteOptions<IndexDocument>> & {
  className?: string;
  initialQueryParams?: string | null;
  filter?: string | null;
};

export function Autocomplete({
  className,
  initialQueryParams,
  filter,
  ...autocompleteProps
}: AutocompleteProps) {
  const autocompleteContainer = useRef<HTMLDivElement>(null);
  const panelRootRef = useRef<ReturnType<typeof createRoot> | null>(null);
  const rootRef = useRef<
    Element | DocumentFragment | Document | ShadowRoot | null
  >(null);
  const router = useRouter();
  const searchParams = useSearchParams();
  const query = searchParams.get("query");
  const [searchTerm, refine] = useState(query ?? "");

  const fallbackQuery = useMemo(
    () => getSearchParamsFromUrl(initialQueryParams),
    [initialQueryParams],
  );

  const getSources = getSourcesWithFilters({
    filters: filter ?? undefined,
  });

  const plugins = useMemo(() => {
    // First plugin, shows recent searches
    const recentSearchPlugin = createLocalStorageRecentSearchesPlugin({
      key: "instantsearch",
      limit: 3,
      transformSource({ source }) {
        return {
          ...source,
          onSelect({ item }) {
            router.push(`/search?query=${item.label}`); // Navigate to search page with the selected recent search, works both with mouse click and keyboard enter
          },
          getItems(params) {
            if (!params.state.query) return [];
            return source.getItems(params);
          },
        };
      },
    });
    return [recentSearchPlugin];
  }, [router]);

  useEffect(() => {
    if (!autocompleteContainer.current) return;
    const autocompleteInstance = autocomplete({
      ...autocompleteProps,
      container: autocompleteContainer.current,
      initialState: { query: searchTerm },
      plugins,
      getSources: getSources,
      onReset: () => refine(""),
      onSubmit: ({ state }) => {
        if (state.query.match(/^\d{13}$/))
          router.push(`/boker/isbn-${state.query}`);
        else if (state.query) router.push(`/search?query=${state.query}`);
        else if (fallbackQuery.size > 0)
          router.push(`/search?${fallbackQuery.toString()}`);
        else router.push("/search");
      },
      onStateChange: ({ prevState, state }) => {
        if (prevState.query !== state.query) refine(state.query);
      },
      navigator: {
        navigate: ({ itemUrl }) => router.push(itemUrl), // Navigate to book product page with keyboard enter
      },
      renderer: {
        createElement,
        Fragment,
        render: () => {},
      },
      render: ({ children }, root) => {
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root;
          panelRootRef.current?.unmount();
          panelRootRef.current = createRoot(root);
        }
        panelRootRef.current.render(children);
      },
    });
    return () => {
      autocompleteInstance.destroy();
    };
    // Ignorning as autocompleteProps and query will cause flickering
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plugins, router]);

  return (
    <div
      style={{ "--cvr-search-padding-x": 0 } as CSSProperties}
      className={classnames(
        "relative w-full rounded-[var(--cvr-search-border-radius)]",
        // iOS zooms in on focused inputs smaller than 16px, so keep 1rem on touch devices
        "[--cvr-search-font-size:0.875rem] pointer-coarse:[--cvr-search-font-size:1rem]",
        "[&_.aa-Form]:cvr-search!",
        "[&_.aa-Input]:[font-size:var(--cvr-search-font-size)]! [&_.aa-Input]:placeholder-[var(--cvr-search-placeholder)]!",
        "[&_.aa-SubmitIcon]:text-[var(--cvr-search-placeholder)]!",
        "[&_.aa-SubmitButton]:pl-4!",
        className,
      )}
      ref={autocompleteContainer}
    />
  );
}
